import type { CostBar } from './costbar.ts';
import type { ProjectCompletenessDto } from './projectDetails.ts';
import type { SitePlace } from './place.ts';
import type { FunnelDto, MarkEstimate } from './funnel.ts';
import type { NowDto } from './process/client.ts';
import type { SeasonSpan } from './seasons.ts';
import type { PhaseKey } from './process/phases.ts';
import type { NodeOverrides, OverrideField } from './process/overrides.ts';
import type { ProcessKept } from './process/standing.ts';
import type { DecisionChoose, ProcessTreeDoc, TaskOwner, TreeOption } from './process/schema.ts';
import type { PerformerKind } from './process/trades.ts';
import type { SpecialKey, SpecialSettings } from './process/specials.ts';
import type { PatternWeight } from './patterns/language.ts';
import type { BuildingTypeKey } from './patterns/patterns.ts';
import type { ProjectSection } from './sections.ts';

/**
 * Wire types shared by the API and the web app. The API is the only place
 * that decides what a role may see; these shapes are already filtered.
 */

export const PROJECT_STATUSES = ['active', 'paused', 'closed', 'cancelled', 'archived'] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];
/**
 * A project's status in a word, as every screen says it. It changes only by an event, never chosen (Jason, 2026-10-06;
 * Still to decide, A), which events as it stands being core `statusRefusal` (status.ts): active by default, paused and
 * resumed with why, cancelled, completed (`closed`) when Final payment and sign-off is done, archived once completed or
 * cancelled.
 */
export const PROJECT_STATUS_WORDS: Record<(typeof PROJECT_STATUSES)[number], string> = { active: 'Active', paused: 'Paused', closed: 'Completed', cancelled: 'Cancelled', archived: 'Archived' };
export const ENDED_BY = ['client', 'nsb'] as const;
export type EndedBy = (typeof ENDED_BY)[number];
/** A project that ended before it was finished. Clients see when and who, not NSB's note of why. */
export interface ProjectEndDto {
  at: string;
  by: EndedBy;
  reason: string | null;
}

export const TASK_STATES = ['not_started', 'available', 'in_progress', 'done'] as const;
export type TaskState = (typeof TASK_STATES)[number];

export type OrgRole = 'admin' | 'staff';
export type ProjectRole = 'contractor' | 'client' | 'sub';
/** The role a request acts with on one project. Org admins act as contractors everywhere. */
export type ViewerRole = 'contractor' | 'client';

/**
 * Developer mode: an org admin sees the app as a client or a sub would, with
 * the API applying that role's permissions (the X-View-As request header).
 * Read-only except on sample projects.
 */
export const VIEW_AS_MODES = ['client', 'sub'] as const;
export type ViewAs = (typeof VIEW_AS_MODES)[number];

/** How often a person is emailed about what waits on them (spec "Client Emails" §4): gathered unless they choose. */
export const EMAIL_CADENCES = ['each', 'gathered', 'morning'] as const;
export type EmailCadence = (typeof EMAIL_CADENCES)[number];

export interface MeDto {
  /** `mustChangePassword`: signed in with a temporary password NSB set; they choose their own before anything else. */
  user: { id: string; email: string; name: string | null; hasPassword: boolean; mustChangePassword: boolean; emailCadence: EmailCadence };
  org: { id: string; name: string; role: OrgRole } | null;
  projects: { id: string; name: string; role: ProjectRole; isSample: boolean }[];
  /** Set while an admin is viewing as a client or sub; `org` is null meanwhile. */
  viewAs: ViewAs | null;
  /** Org admins can switch views (developer mode). */
  canViewAs: boolean;
}

/** How long a link to choose a new password works: once, for this long. */
export const RESET_MINUTES = 30;

/**
 * Forgot your password (Jason, 2026-10-08: "sends a one time email link and if the email doesn't exist it says so"):
 * a link asked for an address an account uses (one no account uses is refused, `no_account`). `sent` is false when the
 * email did not go out; development shows this machine the link instead (`devLink`).
 */
export interface ResetAskedDto {
  ok: true;
  sent: boolean;
  devLink?: string;
}

/** A reset link opened on its page: the account it chooses a password for. Opening it spends nothing. */
export interface ResetLinkDto {
  email: string;
}

/** A new password chosen through a reset link: signed in, where to go next, and how many other sessions it ended. */
export interface ResetDoneDto {
  ok: true;
  redirect: string;
  signedOut: number;
}

export interface PipelineItemDto {
  id: string;
  name: string;
  /** A lead made from an email by itself, not opened by NSB yet (spec "Intake" §4). */
  fromEmail: boolean;
  code: string | null;
  status: ProjectStatus;
  isSample: boolean;
  converted: boolean;
  siteAddress: string | null;
  contactName: string | null;
  /** What the project is, in a sentence or two. */
  summary: string | null;
  /** Its small watercolor, once painted: the setting its name and summary suggest, never the building. */
  icon: ArtDto | null;
  /** For a running project, the gate or landmark it is working toward, and the step it is on for it. */
  next: { stepName: string | null; gateName: string } | null;
  /** Where a running project is: the phase its process is in (core `stagePhase`), as its tree names it; the Projects page groups by it. */
  stage: ProjectStage | null;
  /** Its money bar, drawn small on the Projects page (design study 04, E-A). */
  bar: CostBar | null;
  updatedAt: string;
}

export interface PipelineDto {
  leads: PipelineItemDto[];
  /** Set-up projects in progress (active or paused). */
  projects: PipelineItemDto[];
  /** Set-up projects that have closed or been cancelled. */
  ended: PipelineItemDto[];
  /** Icons are being painted: the page looks again shortly. */
  painting: boolean;
}

/** A lead as intake proposes it, or as Jason confirms it (spec §8: a name, a contact, a site and a source note). */
export interface LeadFields {
  name: string;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  site: string | null;
  summary: string | null;
  sourceNote: string | null;
}

/** Mail to the intake address, read: a proposed lead waiting for Jason (spec §6: nothing enters without confirmation). */
export interface IntakeItemDto {
  id: string;
  /** 'lead' when it reads as a new inquiry; null for other mail, which waits for the rest of intake (phase 2). */
  kind: 'lead' | null;
  receivedAt: string | null;
  from: string | null;
  subject: string | null;
  proposal: LeadFields;
}

/** The leads side of the intake address: where to forward, whether it is being read, and what waits. */
export interface LeadIntakeDto {
  /** The intake alias leads are forwarded to, once Gmail is connected and one is chosen. */
  address: string | null;
  /** Why forwarded mail cannot be read yet, in plain words; null when it can. */
  blocked: string | null;
  /** When the intake address was last read. */
  checkedAt: string | null;
  /** Emails fetched and still being read into proposals: the page looks again shortly. */
  reading: number;
  items: IntakeItemDto[];
  /** How many emails wait on NSB on the Intake page (spec "Intake" §5). */
  waiting: number;
}

/** An email on the Intake page that waits on NSB: what it is, in a line, and the lead it would be. */
export interface IntakeEmailDto {
  id: string;
  receivedAt: string | null;
  from: string | null;
  subject: string | null;
  why: string | null;
  sort: 'lead' | 'project' | 'unsure' | 'not' | null;
  proposal: LeadFields;
}

/** What intake did by itself this week: a lead made, an email kept with a project, or one set aside, each with Undo. */
export interface IntakeDoneDto {
  id: string;
  what: 'made' | 'kept' | 'set_aside';
  receivedAt: string | null;
  from: string | null;
  subject: string | null;
  why: string | null;
  project: { id: string; name: string; archived: boolean } | null;
}

/** The Intake page (spec "Intake" §5): what could not be settled by itself, then what was done by itself this week. */
export interface IntakePageDto {
  address: string | null;
  blocked: string | null;
  checkedAt: string | null;
  reading: number;
  waiting: IntakeEmailDto[];
  done: IntakeDoneDto[];
}

/** The next gate or landmark not yet reached, and about when (the process's schedule, or its plan). */
export interface MilestoneDto {
  id: string;
  key: string | null;
  name: string;
  kind: 'gate' | 'milestone' | 'decision';
  on: string | null;
  clientNote: string | null;
  /** The step working toward it now. */
  stepName: string | null;
}

/**
 * What the client sees of a project, part by part (feature spec "client page"
 * §1): the price (sentence and cost bar), the funnel, and the process with the
 * decisions it declares. The process is NSB's draft until it is published.
 */
export interface ClientShows {
  price: boolean;
  funnel: boolean;
  process: boolean;
}

export const CLIENT_SHOWS_ALL: ClientShows = { price: true, funnel: true, process: true };

/** The money bar (core `costbar.ts`): billed over money in, with the client's budget as lines on it. */
export interface CostBarDto {
  /** Null while nothing is billed and nothing has come in. */
  bar: CostBar | null;
  sentence: string;
  /** The client's budget, once both sides have confirmed it: the upper limit drawn on the bar. */
  ceiling: BudgetCeiling | null;
  /** A budget one side proposed and the other has not answered: drawn with its contingency, dotted, named pending; its answer is on the Budget page. */
  pendingBudget: (BudgetCeiling & { byRole: 'client' | 'contractor' }) | null;
  /** NSB's own job: billed and money in only, no budget (design study 04, 10A). */
  internal: boolean;
}

/**
 * Where a budget's contingency sits (Jason, 2026-10-07, L-9): `inside` the amount, all in ($400,000 at 10% is $360,000
 * for the work and $40,000 held), or `on_top` of it ($400,000 for the work, $40,000 held, $440,000 all in). Core
 * `splitBudget` makes the split.
 */
export type BudgetReserve = 'inside' | 'on_top';

/** What the client can spend, all in, split into the work and the contingency held (feature spec "client page" §4). */
export interface BudgetCeiling {
  /** All in: the work and the contingency held. */
  totalCents: number;
  /** What is for the work, the contingency held apart from it. */
  workCents: number;
  contingencyCents: number;
  contingencyPct: number;
  /** Where the contingency sits: inside the amount proposed, or on top of it. */
  reserve: BudgetReserve;
}

/** One side's proposal and the other's answer: the two-way record (budget now; change orders later). */
export interface ProposalDto {
  id: string;
  /** The amount proposed: all in when the contingency is inside it, the work's when it is on top. */
  amountCents: number;
  /** The contingency, as proposed: a confirmed budget keeps this split until a new one is confirmed. */
  contingencyPct: number;
  /** Where the contingency sits, as proposed. */
  reserve: BudgetReserve;
  /** The amount split as proposed (core `splitBudget`). */
  split: BudgetCeiling;
  by: string;
  byRole: 'client' | 'contractor';
  note: string | null;
  at: string;
  /** `reserve`: where a confirmation put the contingency, as proposed or switched; null on any other answer, and on a confirmation made before it could be chosen (it reads the proposal's). */
  answer: { answer: 'confirmed' | 'declined' | 'withdrawn'; by: string; byRole: 'client' | 'contractor'; note: string | null; at: string; reserve: BudgetReserve | null } | null;
  /** Waiting on the viewer's side to confirm or decline it. */
  waitingOnYou: boolean;
  /** Proposed by the viewer's side: theirs to withdraw while it waits. */
  yours: boolean;
}

/** The client's budget: the confirmed ceiling, a change waiting, and every proposal before, newest first. */
export interface BudgetDto {
  ceiling: BudgetCeiling | null;
  confirmedAt: string | null;
  pending: ProposalDto | null;
  history: ProposalDto[];
  /** The project's contingency now: the split a new proposal is made at. */
  contingencyPct: number;
}

/** Where a project is, from its process (Jason, 2026-10-06: never chosen; Still to decide, A). */
export interface ProjectStage {
  key: PhaseKey;
  name: string;
}

export interface ProjectDto {
  id: string;
  name: string;
  code: string | null;
  status: ProjectStatus;
  /** Where it is, from its process, for NSB (the project's own read); a lead, or a job with nothing on it yet, has none. */
  stage?: ProjectStage | null;
  isSample: boolean;
  siteAddress: string | null;
  /** The site as a place, when it was picked from the suggestions. Staff only. */
  sitePlace: SitePlace | null;
  jurisdiction: string | null;
  sourceNote: string | null;
  /** What the project is, in a sentence or two. Staff only. */
  summary: string | null;
  convertedAt: string | null;
  /** Null for clients: NSB's margin is never shown to them (spec §2). */
  feePct: number | null;
  contingencyPct: number | null;
  feePctOverride: number | null;
  contingencyPctOverride: number | null;
  /** Set once the project is cancelled. */
  ended: ProjectEndDto | null;
  /** NSB's: a lead made from an email by itself that NSB has not opened yet (spec "Intake" §4). */
  fromEmail?: boolean;
  /** What the client said they could spend: the funnel's width before any estimate. Staff only. */
  budgetLowCents: number | null;
  budgetHighCents: number | null;
  viewerRole: ViewerRole;
  /** What the client sees (NSB's view only; null for a client). */
  clientShows: ClientShows | null;
  /** A client project or an internal one, NSB's own; null before its process starts (and for a client). */
  processKind: 'client' | 'internal' | null;
  /** The sections the job has, from what it is and the steps it has (core `projectSections`), in the menu's order. */
  sections: ProjectSection[];
}

/**
 * A generated atmosphere image (style guide v2 §13C): the landscape and season
 * of the project's place, never a picture of NSB's work.
 */
export interface ArtDto {
  id: string;
  /** Served as `${url}?w=<width>`, one per entry in `widths` (ascending). */
  url: string;
  widths: number[];
  width: number;
  height: number;
  alt: string;
  /** 1–12 for a project's monthly banner. */
  month: number | null;
}

export const ART_STATUSES = ['pending', 'ready', 'failed', 'retired'] as const;
export type ArtStatus = (typeof ART_STATUSES)[number];

/** One month's banner as the admin sees it: the image once ready, or why not. */
export interface ArtRowDto {
  id: string;
  month: number;
  status: ArtStatus;
  createdAt: string;
  error: string | null;
  art: ArtDto | null;
}

/** The funnel's seasons: a span of the calendar per strip (core `seasons.ts`), rolling land across or its plants down. */
export const SEASON_KINDS = ['seasons_across', 'seasons_down'] as const;
export type SeasonKind = (typeof SEASON_KINDS)[number];

/** A strip ready for the funnel: its span of months and the month it starts; `variant` tells apart the years painted before spans (each a year from January). */
export interface SeasonStripDto extends ArtDto {
  span: SeasonSpan;
  month: number;
  variant: number;
}

/** One strip as the admin sees it: the image once ready, or why not. */
export interface SeasonArtRowDto {
  id: string;
  kind: SeasonKind;
  variant: number;
  span: SeasonSpan;
  /** The month it starts: January for a year or more. */
  month: number;
  status: ArtStatus;
  createdAt: string;
  error: string | null;
  art: ArtDto | null;
}

/** Everything behind a project's banners: the place, what research found, the twelve months and the funnel's seasons. */
export interface ProjectArtDto {
  place: string | null;
  research: {
    summary: string;
    /** The wild plants of the place the season strips draw on, by name. */
    plants: string[];
    sources: { url: string; title: string | null }[];
    researchedAt: string;
  } | null;
  /** Research into the place is still under way; the months follow it. */
  researching: boolean;
  months: ArtRowDto[];
  seasons: SeasonArtRowDto[];
  /** The project's icon for the pipeline, painted from its name and summary. */
  icon: { id: string; status: ArtStatus; createdAt: string; error: string | null; art: ArtDto | null } | null;
  /** Null when generation can start; otherwise the plain reason it cannot. */
  blocked: string | null;
  /** It has no banners of its own yet, so it shows the organization's, painted from where NSB is. */
  usingDefault: boolean;
}

/** Art for the doors of the portal: the login and the empty-state reward. */
export interface BrandArtDto {
  login: ArtDto | null;
  emptyReward: ArtDto | null;
}

/** The process tree's painted pictures, one a step, for the drawing in Settings and on every project (Jason, 2026-10-03). */
export interface StepIconsDto {
  icons: { key: string; url: string }[];
  /** Being painted now. */
  painting: number;
  /** Steps of the tree, and the gates, with no picture yet and none being painted. */
  missing: number;
  /** Why none can be painted now, in words (OpenAI not connected); null when they can. */
  blocked: string | null;
}

/**
 * The pattern pictures (spec "Pattern Mapping" §4.7), for Settings → Development tools: each pattern of the pools that
 * has one, at the organization's own address, how many are being painted, how many have none and none on its way, and
 * why none can be painted now (OpenAI not connected); null when they can.
 */
export interface PatternIconsDto {
  icons: { n: number; url: string }[];
  painting: number;
  missing: number;
  /** Of those missing, how many could not be painted (their newest painting failed for good): Paint tries them again. */
  failed: number;
  /** How many patterns get a picture: the pools' patterns. */
  total: number;
  blocked: string | null;
}

/** A job's pattern pictures, by the pattern's number, each at the project's own address: a client reads them as they read its page. */
export interface PatternPicturesDto {
  pictures: { n: number; url: string }[];
}

/** One thing waiting on NSB, and where it is done (Jason, 2026-10-03: "take me right to the comment"). */
export interface NeedDto {
  text: string;
  /** The page under the project, "inputs" or "reviews/files/…?thread=…"; null when there is no one place. */
  to: string | null;
}

export interface ProjectHomeDto {
  project: ProjectDto;
  costBar: CostBarDto;
  /** Short, plain things waiting on the contractor, each a way to the work. */
  needs: NeedDto[];
  /** Its details, each added as it is needed, and where (issue #3: started from its title and goal). */
  completeness: ProjectCompletenessDto;
  /** This month's banner, once generated. */
  banner: ArtDto | null;
  /** No banner yet and one is being researched or painted: the page looks again shortly. */
  bannerPainting: boolean;
}

export interface ClientViewDto {
  projectName: string;
  /** Where it is, as plainly as the site allows: "Fletcher, Vermont". */
  place: string | null;
  /** What NSB shows them on this project; the parts not shown come back empty. */
  shows: ClientShows;
  /** Null while the price is not shown. */
  costBar: CostBarDto | null;
  /** A budget is confirmed, whether or not the bar that draws it is shown: their way round reads Your budget, else Set your budget. */
  budgetSet: boolean;
  banner: ArtDto | null;
  /** Set once the project is cancelled: nothing waits on the client after that. */
  ended: ProjectEndDto | null;
  /**
   * Design work shared with them whose review task is still open, with NSB's note as it was sent (P-24); asked again by
   * sheets NSB added (design study 29), the sheets new in that round by name and the note that went with them.
   */
  reviewsWaiting: { setId: string; title: string; phase: string; iteration: number; note: string | null; added: string[] | null }[];
  /** The sections the job has (core `projectSections`), as NSB's menu reads them. */
  sections: ProjectSection[];
  /** Files and links shared with them, in the project's files. */
  sharedFiles: number;
  /** A budget NSB proposed that waits on them to confirm or decline. */
  budgetWaiting: ProposalDto | null;
  /** What is on the design board. */
  boardItems: number;
  /** What waits on them now: their own steps the work has reached (core `clientNow`); null while the process is not shown. */
  now: NowDto | null;
  /**
   * The deviations NSB proposed that wait on them to confirm or decline (design study 21): a step come late, or a gate
   * to be passed before the rest of its phase is done. Empty while the process is not shown, and once the project ended.
   */
  deviationsWaiting: DeviationWaitingDto[];
  /**
   * NSB's request to lock the job's pattern language (spec "Pattern Mapping" §4.5), waiting on them to lock it or answer
   * Not yet: null before they read the language (its step reached), and once the project has ended.
   */
  patternsWaiting: PatternsWaitingDto | null;
  /** The Introduction (design study 11): opened by itself until this person has opened it; the mark beside the title ringed until they finished it. */
  introduction: IntroductionDto;
  /** The Introduction's "Where it is": the five steps of the organization's tree Jason named, as one chain, drawn as their own process is (Q1). */
  example: ClientProcessDto;
}

/** A request to lock the pattern language, as what waits on the client: who asked, with their note, and when. */
export interface PatternsWaitingDto {
  id: string;
  by: string | null;
  note: string | null;
  at: string;
}

/** Where a person stands with a project's Introduction: never opened, skipped, or read to its end. NSB's preview reads both true. */
export interface IntroductionDto {
  opened: boolean;
  finished: boolean;
}


export interface ContactDto {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  isPrimary: boolean;
  invitedAt: string | null;
  lastLoginAt: string | null;
  /** They can sign in with a password; `mustChangePassword` while it is still the temporary one NSB set. */
  hasPassword: boolean;
  mustChangePassword: boolean;
}

/** The price funnel as the portal sends it: the timeline, the client's budget, and the seasons behind it. */
export interface ProjectFunnelDto extends FunnelDto {
  budget: { lowCents: number; highCents: number } | null;
  /** The client's confirmed budget, all in: the upper limit drawn across the funnel. */
  ceiling: BudgetCeiling | null;
  /** Ready strips, every span and slot: rolling land for the funnel across, plants for it down; the funnel lays the span that suits it. */
  seasons: { across: SeasonStripDto[]; down: SeasonStripDto[] };
  /** Strips are being researched or painted: the funnel looks again shortly. */
  seasonsPainting: boolean;
}

export interface ActualDto {
  id: string;
  occurredOn: string;
  amountCents: number;
  costAmountCents: number | null;
  /** What an older bill said it was for: a print code, and the trade that prints under it (null when none does). New bills say nothing. */
  costCode: number | null;
  costCodeName: string | null;
  reference: string | null;
  note: string | null;
  source: 'manual' | 'xero' | 'daily_log';
  voidedAt: string | null;
  voidReason: string | null;
}

/** What has come in from the client (migration 0039): the money bar's lower sub-bar. Entered by hand until Xero reports payments; voided, never edited. */
export type PaymentKind = 'payment' | 'deposit' | 'retainer' | 'refund';

export interface PaymentDto {
  id: string;
  receivedOn: string;
  amountCents: number;
  kind: PaymentKind;
  reference: string | null;
  note: string | null;
  voidedAt: string | null;
  voidReason: string | null;
}

/** The Billing page: what was billed and what came in. */
export interface BillingDto {
  bills: ActualDto[];
  payments: PaymentDto[];
  /** What they come to, as the money bar sums and says them (core `computeCostBar`, `costBarSentence`): one rule for both. */
  costBar: CostBarDto;
}

export type IntegrationProvider = 'gmail' | 'resend' | 'xero' | 'signing' | 'openai' | 'claude' | 'google_places';

export interface IntegrationDto {
  provider: IntegrationProvider;
  status: 'connected' | 'error' | 'not_connected';
  accountLabel: string | null;
  connectedAt: string | null;
  lastError: string | null;
  intakeAlias: string | null;
  /** Can this deployment connect it at all (OAuth app configured, etc.). */
  connectable: boolean;
  /** One plain line about what connecting does today. */
  note: string;
  /** When it was last found working, or found not to (checked every night, and on Check now). */
  checkedAt: string | null;
  /** That, in words: "Checked today at 12:01 a.m.: it works.", or since when it has not. */
  checkWords: string | null;
}

/** Who the portal's own mail comes from (Settings → Connections → Gmail). */
export interface GmailSendAsDto {
  choices: { email: string; name: string | null; isDefault: boolean; isPrimary: boolean; through: string | null }[];
  /** The address the portal sends as now. */
  current: string | null;
  /** Chosen in Settings; null follows Gmail's default. */
  chosen: string | null;
}

/** One release's article on the development blog (public: it never names a client, a project or a person). */
export interface ReleaseDto {
  id: string;
  releasedAt: string;
  title: string;
  /** Plain language, first. */
  summary: string;
  features: { title: string; detail: string }[];
  fixes: { title: string; detail: string }[];
}

export interface ReleasesDto {
  releases: ReleaseDto[];
}

/** A connected account that has stopped working (the nightly check found it so): the notice at the top for admins. */
export interface KeyAttentionDto {
  provider: IntegrationProvider;
  name: string;
  /** When it was first found not working. */
  since: string | null;
  /** A pasted key is renewed; a sign-in (Gmail, Xero) is connected again. */
  renew: 'key' | 'sign-in';
  urgent: boolean;
  words: string;
}

/**
 * A key for Claude (Settings → Development tools): good for the tree builder's draft alone, shown whole only when it
 * is made, revoked and never deleted.
 */
export interface ApiKeyDto {
  id: string;
  name: string;
  /** Its first characters, to tell it apart; never enough to use. */
  prefix: string;
  scopes: 'builder'[];
  createdAt: string;
  createdBy: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

/**
 * The Allow page's check of a request to connect Claude (`GET /api/oauth/authorize`): who is asking, and whether the
 * person signed in may allow it (staff of an organization; anyone else is told it is for NSB's staff for now).
 */
export interface OAuthCheckDto {
  clientName: string;
  allowed: boolean;
}

/**
 * A connection of Claude to the person's account, as Account lists it with Revoke (spec "Claude Connector" §3.5): the
 * client's name, when it was allowed and when it last read. Only ones that still work are listed.
 */
export interface ConnectionDto {
  id: string;
  clientName: string;
  createdAt: string;
  lastUsedAt: string | null;
}

/**
 * Settings → Development tools → Test projects (Jason, 2026-10-08: "we leave the test projects in development so i can
 * play with them during development, with an option to reset the test projects under development tools"): the
 * documented jobs, each rebuilt through the process from its recipe. Development against this machine's own database
 * alone; an admin's.
 */
export interface TestProjectsDto {
  available: boolean;
  /** Why it is not available here, in words; null when it is. */
  why: string | null;
  /** The jobs a reset makes, in the order it plays them; empty where it is not available. */
  recipes: { label: string; name: string; calls: number }[];
  /** How many test projects the organization has now. */
  testProjects: number;
  /** The reset under way, or the last one; null before the first. */
  reset: TestProjectsResetDto | null;
}

export interface TestProjectsResetDto {
  running: boolean;
  /** ISO. */
  startedAt: string;
  /** ISO; null while it runs. */
  finishedAt: string | null;
  /** Who started it, by name. */
  by: string | null;
  /** How many test projects it deleted. */
  deleted: number;
  jobs: TestProjectJobDto[];
  /** What stopped it before it was through, in words; null when nothing did. */
  error: string | null;
}

export interface TestProjectJobDto {
  label: string;
  name: string;
  state: 'waiting' | 'playing' | 'made' | 'failed';
  /** The project it made; null until it is made, and for one that failed (deleted again). */
  projectId: string | null;
  /** Made, where its end differs from the recipe's record of it: a line each. */
  differences: string[];
  /** Failed: the call refused (its number in the recipe, counted from 1; 0 for making the project), and the refusal in words. */
  failed: { call: number; method: string; path: string; status: number; refusal: string } | null;
  /** How long its play took; null until it is played. */
  seconds: number | null;
}

export interface OrgDto {
  id: string;
  name: string;
  feePct: number;
  contingencyPct: number;
  /**
   * Where NSB is (Jason, 2026-10-06), kept as a project's site: the words, and the place when picked. The banners every
   * project shows until it has its own are painted from it; only its town and state ever leave the portal.
   */
  placeAddress: string | null;
  place: SitePlace | null;
  defaultArt: DefaultArtDto;
}

/** The organization's own banners and strips: painted from where it is, shown on every project with none of its own yet. */
export interface DefaultArtDto {
  /** The town and state they are painted from; null until where NSB is is picked from the suggestions. */
  place: string | null;
  /** Months painted, of twelve. */
  months: number;
  /** Year strips painted for the funnel, of six. */
  strips: number;
  painting: boolean;
  failed: number;
  /** This month's (or the nearest painted), for a look. */
  banner: ArtDto | null;
  /** Null when they can be painted; otherwise the plain reason they cannot. */
  blocked: string | null;
}

export type FeedbackKind = 'issue' | 'comment';
/** An issue is a bug or a feature request; both are about the software and private to NSB. */
export type IssueType = 'bug' | 'feature';
/**
 * Where a report is (migration 0062): received (a feature: new); planned, the work queue, a switch on every report and
 * the `planned` label on its GitHub issue; fixed (a feature: shipped), once it is built, tested and seen working.
 */
export type IssueStatus = 'received' | 'planned' | 'fixed';

export interface FeedbackReplyDto {
  id: string;
  authorName: string;
  body: string;
  createdAt: string;
  mine: boolean;
}

export interface FeedbackDto {
  id: string;
  /** Its number in the database it was filed in, never given again (migration 0060). */
  number: number;
  /** How it is addressed: the number after a letter for that database, "L-7" on this machine, "P-12" on the live portal. */
  address: string;
  kind: FeedbackKind;
  submittedAs: FeedbackKind;
  issueType: IssueType;
  body: string;
  projectId: string | null;
  projectName: string | null;
  recordType: string | null;
  recordId: string | null;
  recordLabel: string | null;
  screen: string | null;
  reporterName: string;
  reporterRole: string;
  /**
   * Filed by an admin while seeing the portal as a client or a sub (developer
   * mode): the reporter is still the admin, and this says which view it came
   * from. Shown to NSB and the reporter; null otherwise.
   */
  viewedAs: ViewAs | null;
  issueStatus: IssueStatus;
  githubIssueUrl: string | null;
  hasScreenshot: boolean;
  createdAt: string;
  replies: FeedbackReplyDto[];
  mine: boolean;
}

/** GET /api/feedback/reports: Bugs and requests, an admin's. */
export interface ReportsDto {
  /** Every report still open, whatever its age, and the newest of those done (a bug fixed, a feature shipped). */
  reports: FeedbackDto[];
  /** How many of each kind are done, listed or not: more than are listed when the oldest done are left out. */
  done: { bug: number; feature: number };
}

/** GET /api/health: which API is answering, and the build it runs. */
export interface HealthDto {
  ok: true;
  env: 'development' | 'test' | 'production';
  build: { sha: string; ref: string };
}

export interface NotYetBuiltDto {
  error: 'not_yet_built';
  phase: 2 | 3 | 4;
  feature: string;
  message: string;
}

export interface ApiErrorDto {
  error: string;
  message: string;
  details?: unknown;
}

/** The timer as its owner sees it, from any page or device (spec §11: the portal keeps time, Xero never does). */
export interface TimerDto {
  /** Null until someone says what it is for: that can come before, during or after. */
  projectId: string | null;
  projectName: string | null;
  taskId: string | null;
  taskTitle: string | null;
  startedAt: string;
  note: string | null;
}

/** One piece of logged time. Hours only for now; rates come with invoicing. */
export interface TimeEntryDto {
  id: string;
  /** Null until it is given a project. */
  projectId: string | null;
  projectName: string | null;
  taskId: string | null;
  taskTitle: string | null;
  /** The step at the top of the process that holds its task. */
  stepName: string | null;
  userName: string;
  /** When it started; for an entry typed in, the day it was for. */
  startedAt: string;
  /** The day it counts on, Eastern. */
  day: string;
  minutes: number;
  note: string | null;
  billable: boolean;
  source: 'timer' | 'manual';
}

/** A project's time: every entry, newest first, and the totals by the step at the top it was logged under. */
export interface ProjectTimeDto {
  totalMinutes: number;
  byStep: { stepName: string; minutes: number }[];
  entries: TimeEntryDto[];
}

/** One person's week, Monday to Sunday: each day's entries across projects. */
export interface WeekTimeDto {
  /** The Monday, YYYY-MM-DD. */
  start: string;
  totalMinutes: number;
  byProject: { projectId: string; projectName: string; minutes: number }[];
  entries: TimeEntryDto[];
  /** Every entry of theirs, from any week, still waiting to be given a project. */
  toAssign: TimeEntryDto[];
}

// --- Reviews: design work shared for the client's comments and marks -----------

/** Where a mark sits on a file, measured 0..1 across and down it. */
export type MarkShape =
  | { type: 'pin'; x: number; y: number }
  | { type: 'box' | 'ellipse'; x: number; y: number; w: number; h: number }
  | { type: 'path'; points: [number, number][] }
  | { type: 'arrow'; x1: number; y1: number; x2: number; y2: number };

/**
 * A project's asset (feature spec "client page, files" §2): a file uploaded to
 * the project, or a link to one (Drive), in a review iteration or not. Every
 * asset is a reviewable item: marks and a conversation.
 */
export interface ReviewFileDto {
  id: string;
  /** The review iteration it is part of; null while it is only in the project's files. */
  setId: string | null;
  kind: 'file' | 'link';
  name: string;
  /** Null for a link. */
  contentType: string | null;
  sizeBytes: number | null;
  /** Where a link goes (a Drive file or folder); null for a file. */
  url: string | null;
  /** The web-sized image to view and mark up; null for anything that is not an image. */
  viewUrl: string | null;
  /** A small copy of the image, for lists. */
  thumbUrl: string | null;
  /** The file as it came; null for a link. */
  originalUrl: string | null;
  width: number | null;
  height: number | null;
  note: string | null;
  /** The day it is from, when older than the day it was added (a signed proposal, a survey). */
  dated: string | null;
  /** Where it came from on the web: the picture's address, or the page it was on (a pin). */
  sourceUrl: string | null;
  /** The client sees it. */
  shared: boolean;
  /**
   * Put into an iteration already shared and not sent to its reviewers yet (design study 29): NSB's alone until it is
   * sent with the rest of its round, with NSB's note. A client never gets one.
   */
  unsent: boolean;
  /** The sheet of its iteration this one revises (design study 29); the earlier one stays, every comment on it kept. */
  revises: { id: string; name: string } | null;
  /** The sheet of its iteration that revises this one; null while it stands. */
  revisedBy: { id: string; name: string } | null;
  /** The round it went to the reviewers in, after its iteration was shared, and NSB's note with it; null for one shared with it. */
  round: { id: string; at: string; by: string; note: string | null } | null;
  /** Threads still open, and all threads. */
  openThreads: number;
  threads: number;
  createdAt: string;
}

/** An asset as the project's files list it: where it sits, and what NSB can still do with it. */
export interface AssetDto extends ReviewFileDto {
  /** The review iteration it was added to. */
  review: { setId: string; phase: string; iteration: number; title: string; shared: boolean } | null;
  addedBy: string | null;
  /** Who added it: a client's file is on the project for both sides from the start. */
  addedRole: 'client' | 'contractor';
  /** The viewer added it. */
  mine: boolean;
  /**
   * The viewer can still take it out: theirs, nothing said on it, in no
   * iteration, never on the design board, in no history entry (and for NSB,
   * never shared).
   */
  removable: boolean;
  /** It is on the design board. */
  onBoard: boolean;
}

// --- The design board (process-flow spec "background"; built 2026-10-03) ------------------------------

/**
 * One thing on a project's design board, a free canvas both sides arrange by
 * hand: a picture or a link (one of the project's files, open it to mark it
 * up and talk about it), or a note that names a cluster.
 */
export interface BoardItemDto {
  id: string;
  kind: 'image' | 'link' | 'note';
  /** Where it sits, in board units (pixels at full size), its width, and its place in the stack. */
  x: number;
  y: number;
  w: number;
  z: number;
  /** A note's words, or a picture's caption. */
  caption: string | null;
  by: string;
  byRole: 'client' | 'contractor';
  at: string;
  /** The viewer can take it off the board: their own, or anything for NSB. */
  removable: boolean;
  asset: {
    id: string;
    name: string;
    thumbUrl: string | null;
    viewUrl: string | null;
    width: number | null;
    height: number | null;
    /** A link's address. */
    url: string | null;
    sourceUrl: string | null;
    threads: number;
    openThreads: number;
  } | null;
}

export interface BoardDto {
  items: BoardItemDto[];
}

/** An iteration of design work under a phase ("Concept", iteration 1): its files and the client's review task. */
export interface ReviewSetDto {
  id: string;
  phase: string;
  iteration: number;
  title: string;
  note: string | null;
  /** Null while it is a draft only NSB sees. */
  sharedAt: string | null;
  /** Who shared it, by name: whose note `note` is once it is shared (P-24). */
  sharedBy: string | null;
  task: { id: string; state: TaskState } | null;
  /** The step it is on is turned off on the job: finishing the review then writes nothing to it (B1). */
  stepGone: 'off' | null;
  files: ReviewFileDto[];
  /** The people asked to review it; it is reviewed when every one of them is done. */
  reviewers: ReviewerDto[];
  /** Shared, with reviewers, and every one of them done. */
  reviewed: boolean;
  /** The viewer's own part: asked and not done yet, done, or not asked (null; NSB always). */
  yours: 'waiting' | 'done' | null;
  /** What was said of the iteration as a whole, oldest first. */
  comments: ReviewCommentDto[];
  /** Claude's reading of the finished review: NSB's alone, null for a client and until it is reviewed. */
  summary: ReviewSummaryDto | null;
  /** The last time NSB opened it again for comments after it was reviewed, and why. */
  reopened: { at: string; by: string; reason: string } | null;
  /**
   * The work has reached its step (core `reachedFor`), or it is shared: open for its files. A draft not reached yet is
   * named under Later in the process, never opened (P-22, design study 27).
   */
  reached: boolean;
  createdAt: string;
}

export interface ReviewerDto {
  contactId: string;
  name: string;
  /** They have a sign-in, so they can review it themselves; NSB can say they are done for anyone. */
  canSignIn: boolean;
  doneAt: string | null;
  /** Who said they were done, when it was not them: NSB, for them. */
  doneBy: string | null;
  /**
   * When a new round asked them again, having said they were done before it (a sheet added, design study 29, asked A;
   * or the iteration opened again); null when it has not.
   */
  askedAgain: string | null;
}

export interface ReviewCommentDto {
  id: string;
  authorName: string;
  authorRole: 'client' | 'contractor';
  mine: boolean;
  body: string;
  createdAt: string;
}

/** A finished review summed up for the designer: plainly, by file and comment number. */
export interface ReviewSummaryDto {
  status: 'writing' | 'ready' | 'failed';
  /** Why there is none, in words (Claude not connected, or it failed). */
  error: string | null;
  overall: string | null;
  points: { text: string; fileId: string | null; fileName: string | null; marks: { number: number; threadId: string }[] }[];
  questions: string[];
  next: string[];
  createdAt: string;
  /** NSB has taken it in; until then it waits on NSB's home. */
  read: boolean;
}

/** One version of a mark or reply. */
export interface MarkDto {
  id: string;
  markId: string;
  threadId: string;
  version: number;
  authorName: string;
  authorRole: 'client' | 'contractor';
  mine: boolean;
  shape: MarkShape | null;
  body: string | null;
  withdrawn: boolean;
  createdAt: string;
}

export interface MarkThreadDto {
  threadId: string;
  /**
   * Its number across its iteration, in the order threads were started on any of its sheets (design study 29, numbers
   * A: the first comment on a sheet added later goes on from the last); for a file in no iteration, on the file. Kept
   * for good: a withdrawn comment keeps its number, and a later one never takes it.
   */
  number: number;
  resolved: boolean;
  /** Who resolved it last, and when; null while it is open. */
  resolvedBy: string | null;
  resolvedAt: string | null;
  /** The current version of the first mark, and of each reply after it. */
  root: MarkDto;
  replies: MarkDto[];
}

/** One line of a file's history: every mark, reply, edit, withdrawal and resolution, in order. */
export interface MarkEventDto {
  at: string;
  who: string;
  role: 'client' | 'contractor';
  what: 'marked' | 'replied' | 'edited' | 'withdrew' | 'resolved' | 'reopened';
  number: number;
  body: string | null;
  /** What an edit replaced. */
  before: string | null;
}

export interface FileMarksDto {
  /** The asset, the iteration it is in (all null for one only in the project's files), and the others beside it. */
  file: ReviewFileDto & { setTitle: string | null; phase: string | null; iteration: number | null; siblings: { id: string; name: string }[] };
  threads: MarkThreadDto[];
  history: MarkEventDto[];
  /** NSB resolves threads; clients read that they are. */
  canResolve: boolean;
  /** In an iteration everyone has reviewed: closed to comments until NSB opens it again. */
  locked: boolean;
}


// --- The timeline: everything that has happened on a project (feature spec "client page" §5) ---------

/**
 * What a dated entry records: something that happened before the portal, or outside it, on the project or on one of its
 * steps' logs (design study 31, slice 3). One list of fifteen, offered alike on a step's log and the timeline, in this
 * order (Jason, 2026-10-29: "let's keep the full list of 15").
 */
export const HISTORY_KINDS = [
  'note',
  'call',
  'meeting',
  'visit',
  'email',
  'sent',
  'received',
  'photos',
  'document',
  'report',
  'proposal',
  'quote',
  'agreement',
  'invoice',
  'payment',
] as const;
export type HistoryKind = (typeof HISTORY_KINDS)[number];
export const HISTORY_KIND_WORDS: Record<HistoryKind, string> = {
  note: 'Note',
  call: 'Call',
  meeting: 'Meeting',
  visit: 'Site visit',
  email: 'Email',
  sent: 'Sent',
  received: 'Received',
  photos: 'Photos',
  document: 'Document',
  report: 'Report',
  proposal: 'Proposal',
  quote: 'Quote',
  agreement: 'Agreement',
  invoice: 'Invoice',
  payment: 'Payment',
};
/** The kinds that carry an amount and who it is from: a quote, an invoice. */
export const HAS_AMOUNT: readonly HistoryKind[] = ['quote', 'invoice'];

/** Something that happened, written down by NSB on the day it happened. */
export interface HistoryEntryDto {
  id: string;
  happenedOn: string;
  /** 'HH:MM', Eastern, when known. */
  happenedAt: string | null;
  kind: HistoryKind;
  title: string;
  body: string | null;
  /** Where it is on record: a Gmail thread, a Drive file, a Xero document. NSB's only. */
  sourceUrl: string | null;
  /** Its files, in the project's files, in the order they were put with it (a client sees each once it is shared). */
  files: { id: string; name: string }[];
  shared: boolean;
  /** The step it is on (a step, or one of its tasks), by its one name; null for the project's own. */
  step: { id: string; name: string } | null;
  /** A quote's or an invoice's amount and who it is from: NSB's alone, never a client's to read. */
  amountCents: number | null;
  whoFrom: string | null;
}

/** A step's log, its tasks' with it, newest first. */
export interface StepLogDto {
  entries: HistoryEntryDto[];
}

/** An entry of a step's log shared with the client, as their step's card reads it: never an amount, who from or where on record. */
export interface ClientLogEntryDto {
  id: string;
  happenedOn: string;
  kind: HistoryKind;
  title: string;
  body: string | null;
  /** Its shared files alone. */
  files: { id: string; name: string }[];
}

export type TimelineWhat = 'history' | 'files' | 'review' | 'conversation' | 'mail' | 'process' | 'money' | 'project' | 'people' | 'time';

/** One line of a project's timeline: something done in the portal (the audit log), or a history entry. */
export interface TimelineItemDto {
  id: string;
  /** When, for order. */
  at: string;
  /** The day it happened, Eastern. */
  day: string;
  /** The time of day in words ("2:40 pm"), when known: never for an act whose day was set after the fact. */
  time: string | null;
  /**
   * The day it was recorded, when that is not the day it happened (L-22 A): an act whose day was set after the fact
   * (marked done, passed or done up to here on a day gone by, a day set by its pencil, a project ended on an earlier
   * day) sits on that day, with when it was recorded beside it. Null when the two are the same.
   */
  recordedOn?: string | null;
  what: TimelineWhat;
  /** The line itself: "Jason shared the design proposal with the client." */
  title: string;
  who: string | null;
  /** Several of the same thing by the same person on the same day, as one line. */
  count: number;
  /** What it shows when opened. */
  detail: string[];
  /** Where to see it in the portal. */
  link: string | null;
  /** A history entry, for NSB to change. */
  history: HistoryEntryDto | null;
}

export interface TimelineDto {
  items: TimelineItemDto[];
  /** Ask with ?before= this for what came before; null at the beginning of the project. */
  next: string | null;
}

// --- A project's email (Jason, 2026-10-03: "the portal to be the single source of truth") ----------------

export interface MailPersonDto {
  name: string | null;
  email: string;
}

/** A Gmail thread on a project, or one suggested for it. */
export interface MailThreadDto {
  id: string;
  subject: string | null;
  /** Who is on it besides NSB. */
  people: MailPersonDto[];
  firstAt: string | null;
  lastAt: string | null;
  messageCount: number;
  state: 'suggested' | 'on' | 'off';
  /** The thread in Gmail. */
  gmailUrl: string | null;
}

export interface MailDto {
  /** Gmail is connected (Settings → Connections), and which account is read. */
  connected: boolean;
  account: string | null;
  /** The project's people, whose threads are looked for. */
  people: MailPersonDto[];
  threads: MailThreadDto[];
  /** Threads with the project's people that are not on it yet: NSB says which belong. */
  suggestions: MailThreadDto[];
  checkedAt: string | null;
}

export interface MailMessageDto {
  id: string;
  sentAt: string;
  from: MailPersonDto | null;
  to: MailPersonDto[];
  cc: MailPersonDto[];
  subject: string | null;
  /** What this message says, without the earlier messages it quotes. */
  fresh: string;
  /** All of its text. */
  text: string;
  /** Sent from the connected mailbox. */
  outbound: boolean;
  /** What came with it, now in the project's files. */
  attachments: { id: string; name: string }[];
  gmailUrl: string | null;
}

export interface MailThreadDetailDto {
  thread: MailThreadDto;
  messages: MailMessageDto[];
}

/** Threads found by a search of the mailbox, and whether each is on this project already. */
export interface MailSearchDto {
  results: (MailThreadDto & { gmailThreadId: string })[];
}

// --- Built in dev, copied to production (feature spec "client page" §3) --------------------------------

/**
 * What loading a project's bundle does, or did: what it adds (nothing already
 * here is changed), what the client will newly see, and what the client sees
 * of the project if the bundle has it otherwise.
 */
export interface BundleSummaryDto {
  project: { id: string; code: string; name: string };
  from: { env: string; createdAt: string };
  adds: { files: string[]; links: string[]; iterations: string[]; history: string[]; board: string[]; patterns: string[] };
  /** Items already here, by their permanent ids: left as they are. */
  alreadyHere: number;
  /** What the client sees once it is loaded that they do not see now. */
  clientSees: string[];
  /** What to check before loading: a project name that differs, an iteration that cannot be shared here. */
  warnings: string[];
  /** What the client sees of the project, where the bundle has it otherwise than here; changed only when asked. */
  settings: { now: ClientShows; bundle: ClientShows } | null;
  /** False for a dry run. */
  loaded: boolean;
}

// --- The process tree: where a project is (process-flow spec) -----------------------------------

/** A latest plan for a step (a gate or landmark), with where it started. */
export interface StepPlanDto {
  on: string;
  originalOn: string;
  cause: 'baseline' | 'adjustment' | 'change_order';
  reason: string | null;
}

export interface ProcessStepDto {
  id: string;
  /** The tree step it came from; null for a step the project grew itself. */
  key: string | null;
  /** A named copy of a repeatable step ("Front door"). */
  instance: string | null;
  kind: 'work' | 'milestone' | 'decision' | 'inspection' | 'gate';
  title: string;
  parentId: string | null;
  state: TaskState;
  owner: TaskOwner | null;
  days: number | null;
  /** What it is, for NSB (design study 31, slice 3: "For NSB"). A step's notes and quotes are in its log. */
  why: string | null;
  flagged: boolean;
  /** Turned off, kept for its history. */
  off: boolean;
  trades: string[];
  /** A package: the trade (a key from the org's list) and the one performer whose share of it this is. */
  trade: string | null;
  performer: { id: string; name: string; kind: PerformerKind } | null;
  /** What it includes and leaves out: inclusions, exclusions, durations, crew, lead times; what a bid is for. */
  scopeNote: string | null;
  /**
   * It delivers an estimate (design study 17): the tree says so, and this is the latest set on it, or none yet. A
   * delivering step is not done until one is set.
   */
  estimate: {
    delivers: boolean;
    current: MarkEstimate | null;
    /** Every range set on it, oldest first, each as it stands (a re-dated one by its new day): its rows in Tasks. */
    history: (MarkEstimate & { id: string })[];
  };
  people: { contactId: string; name: string; trade: string | null }[];
  startedOn: string | null;
  doneOn: string | null;
  hardDueOn: string | null;
  softDueOn: string | null;
  /** For a gate: what the client is told, and any open exception (done on an override). */
  gate: {
    clientNote: string | null;
    overrideReason: string | null;
    clearedAt: string | null;
    clearedNote: string | null;
    /**
     * Once done, who did it, from the record its passing wrote (design study 09, gates A): approved by the one who
     * approves it, or marked done by NSB; `by` their name, null when their account has none. `recorded`: the client's
     * approval given outside the portal, recorded by NSB (design study 24, L-16 A): approved by the client, `by` null,
     * the record in `record`.
     */
    passed: { by: string | null; approved: boolean; recorded?: boolean } | null;
    /** The approval record its approval was recorded with (L-16 A), while that is what passed it; NSB's alone. */
    record?: ApprovalRecordDto | null;
  } | null;
  /** For an inspection: the authority's hold, passed, or passed on an override with a reason that stays an open exception. */
  inspection: {
    overrideReason: string | null;
    clearedAt: string | null;
    clearedNote: string | null;
  } | null;
  plan: StepPlanDto | null;
  /** The phase it sits in on the job (core `projectPhases`): the tree's, or the one a step of the job's own was added to. */
  phase: PhaseKey | null;
  /** Logged time, in minutes. */
  minutes: number;
  /** Time, a plan, a review or an estimate line points at it, or it has moved: it is kept if turned off. */
  hasHistory: boolean;
  /** It delivers a design review: done when the review is, never by hand (B1). */
  review: boolean;
  /**
   * It came late (design study 21, land A): added once its own phase had closed, it sits in the phase under way
   * (`phase`); `from` is its own phase, the tree's. Null for a step in its own phase.
   */
  late?: { from: PhaseKey } | null;
  /** The latest deviation on it (a step come late, or a gate passed before its phase is done), or none. */
  deviation?: DeviationDto | null;
  /**
   * A decision's options (design study 22): its tree node's (the master as saved, with the project's own settings over
   * it), whether one is chosen or any, and the latest choice recorded deciding it, or none yet. Null for any other step,
   * and for a decision with no options and no choice recorded.
   */
  decision?: { options: TreeOption[]; choose: DecisionChoose; chosen: DecisionChoiceDto | null } | null;
  /**
   * A step of the master at the top of the job (design study 25): whether it follows the master, taking what a save of
   * the master changes, or keeps its own, changed and saved on this project, since when (ISO); and whether its waits are
   * the project's own (`waits`: a wait drawn or taken out on it, design study 31), which a save of the master leaves as
   * they are until given back. Absent on a step of the job's own, a named copy, and a task in a list.
   */
  follows?: { follows: boolean; since: string | null; waits: boolean };
  /**
   * The special tasks it carries on this job (spec "Pattern Mapping" §3.3), each with whether what it needs is set (for
   * Pattern mapping, what is being built): until it is, the step is Not configured on NSB's trees. Absent on a step that
   * carries none.
   */
  specials?: StepSpecialDto[];
}

/** A pattern on a job's map (spec "Pattern Mapping" §4.3): the book's number, how much it matters, where it sits, its note. */
export interface PatternPickDto {
  n: number;
  weight: PatternWeight;
  /** Its middle on the map, in the map's units. */
  x: number;
  y: number;
  /** Why it matters on this house, as NSB or the client wrote it; null when nobody has. */
  note: string | null;
  /** In the pool of what is being built; false once a building type it came from is unticked (it stays, marked so). */
  inPool: boolean;
  /** Who changed it last, by name, and as whom; null for someone no longer in the portal. */
  changedBy: string | null;
  changedRole: 'contractor' | 'client' | null;
  changedAt: string;
}

/**
 * A job's language (spec §4.3): the step it belongs to, what is being built and every pattern that could touch (its pool,
 * in the book's order), the patterns on the map, and its version, which moves with every change, so a page open on it
 * asks only that every second.
 */
export interface PatternMapDto {
  stepId: string;
  stepTitle: string;
  types: BuildingTypeKey[];
  pool: number[];
  picks: PatternPickDto[];
  version: number;
  /** Nothing changes it: the job is completed or archived, kept as it ended. */
  kept: boolean;
  /** Its lock: once locked, nothing on it changes until NSB opens it again. */
  lock: PatternLockDto;
}

/**
 * A language's lock (spec §4.5), as the record holds it for whoever reads it (`proposals` of kind `patterns` and their
 * answers, `pattern_unlocks`): locked or open; the request to lock it waiting, if one is; the client's last Not yet, until
 * NSB asks again; and the last opening again, while it is open.
 */
export interface PatternLockDto {
  locked: boolean;
  /** Locked: the day it was locked (the day they gave it, for one given in person) and who locked it. */
  lockedOn: string | null;
  lockedBy: string | null;
  /**
   * How it was locked: by the client on the portal, given in person and recorded by NSB, or at NSB's request on NSB's own
   * job. A client reads one given in person as their own (`client`, by nobody named).
   */
  how: 'client' | 'in_person' | 'own_job' | null;
  /** The record of a lock given in person: the day, its evidence and who recorded it. NSB's alone. */
  record: ApprovalRecordDto | null;
  /** NSB's request waiting on the client: on the client, never NSB, to answer. */
  waiting: { id: string; note: string | null; by: string | null; at: string; waitingOnYou: boolean } | null;
  /** The client's Not yet, with their note, until NSB asks again; `mine` when the one reading it is the one who answered. */
  declined: { note: string | null; by: string | null; at: string; mine: boolean } | null;
  /** Opened again after it was locked, and why, while it stays open. */
  reopened: { reason: string; by: string | null; at: string } | null;
  /** NSB's own job: no client, so NSB's request locks it. */
  ownJob: boolean;
  /** The job's client reads the language now (a client's job, its step reached): only then is the client asked to lock it. */
  clientSees: boolean;
  /**
   * The job kept as it ended (core `processKept`): completed (`shape`), its language still locked, asked for, answered
   * and recorded, its step going on to done, but never opened again; archived (`whole`), nothing about it at all.
   */
  kept: 'shape' | 'whole' | null;
}

/**
 * A special task a job's step carries, its settings as they stand (the latest set) or none yet, and whether what it
 * holds the step for is done (Pattern mapping: its language locked, the only way the step is done).
 */
export interface StepSpecialDto {
  key: SpecialKey;
  set: boolean;
  settings: SpecialSettings[SpecialKey] | null;
  done: boolean;
}

/**
 * A client's approval of a gate of theirs given before the portal or outside it, recorded by NSB on the gate's panel
 * (design study 24, L-16 A; Jason, 2026-10-08: "this thing should be considered an approval record and requires either a
 * justification note (most risky), an uploaded document, or -- for the future -- client signoff through the signature
 * portal"): the day it was given and its evidence, a note or one of the project's files or both, with who recorded it.
 * Append-only (`approval_records`, migration 0065). NSB's alone: a client reads the gate approved by them on its day.
 */
export interface ApprovalRecordDto {
  id: string;
  /** The day the client gave it, YYYY-MM-DD: never a day to come. */
  on: string;
  /** Who recorded it, by name; null when their account has none. */
  recordedBy: string | null;
  /** When it was recorded, ISO. */
  recordedAt: string;
  /** NSB's justification: how it knows they approved it. */
  note: string | null;
  /** The document that shows it: one of the project's files (the signed proposal, the email). */
  file: { id: string; name: string } | null;
}

/**
 * What was chosen deciding a decision (design study 22; Jason, 2026-10-07: "either select one of many or many of many"):
 * the options as their words read when chosen, who chose them and as whom, and when. Append-only (`decision_choices`): a
 * decision decided again adds a choice, and the latest is the choice.
 */
export interface DecisionChoiceDto {
  options: TreeOption[];
  /** Who chose, by name; null when their account has none. */
  by: string | null;
  /** The client choosing, or NSB (deciding its own, or marking the client's for them). */
  role: 'client' | 'contractor';
  /** When, ISO. */
  at: string;
}

export const DEVIATION_KINDS = ['late', 'early_gate'] as const;
export type DeviationKind = (typeof DEVIATION_KINDS)[number];

/**
 * A deviation (design study 21, lateRule A, signoff C; Jason, 2026-10-07): a step added once its own phase had closed
 * (`late`), or a gate passed while steps of its phase are not done (`early_gate`). NSB proposes it with a reason and the
 * client confirms or declines it, with a note, the two-way record a budget uses; until confirmed, a late step is held
 * and the gate is not done. On NSB's own job the reason alone confirms it. Append-only (`deviations`,
 * `deviation_answers`).
 */
export interface DeviationDto {
  id: string;
  kind: DeviationKind;
  /** NSB's reason, which the client reads. */
  reason: string;
  proposedAt: string;
  /** Who proposed it, by name. */
  proposedBy: string | null;
  /** Waiting on the client's answer, confirmed (by the client, or by the reason alone on NSB's own job), or declined. */
  answer: 'waiting' | 'confirmed' | 'declined';
  answeredAt: string | null;
  /** What the client wrote with their answer; or, answered by NSB for the client, how the client told NSB (NSB's). */
  note: string | null;
  /** Who answered it, by name; null while it waits, or when their account has none. */
  answeredBy?: string | null;
  /**
   * NSB answered it for the client (design study 24, L-20 B): allowed only while nobody on the job signs in as its
   * client; on the record as NSB's, "for the client".
   */
  forClient?: boolean;
  /** Still an open exception: a late step not done, or a gate passed early whose phase is not done. */
  open?: boolean;
}

/** A deviation waiting on the client, as their Waiting on you lists it: the step by the name they see it by. */
export interface DeviationWaitingDto extends DeviationDto {
  stepId: string;
  /** The step, by the line NSB wrote for the client, else its title. */
  title: string;
  /** The phase it belongs to (late), or the phase it closes (a gate passed early), by the tree's name for it. */
  phase: string;
  /** A late step: the phase it lands in, by name; null for a gate. */
  landsIn: string | null;
}

export interface ProcessLinkDto {
  stepId: string;
  needsId: string;
  kind: 'requires';
}

/**
 * A project's process as its client sees it (Jason, 2026-10-05: "the client
 * view should see the money bar the funnel and then below that the process
 * tree for their project with where things are and what is coming"): every
 * step on the job, drawn as NSB's are, with where it stands and nothing of
 * NSB's own (no sub's name, no money, no notes, no open exception).
 */
/** One of the client's own steps on their Decisions page (core `clientSteps`), with what they said settling it. */
export interface ClientDecisionDto {
  id: string;
  title: string;
  /** A gate is one they approve (design study 09, gates A). */
  kind: 'decision' | 'work' | 'gate';
  /** What the client reads of it. */
  why: string | null;
  doneOn: string | null;
  /** What they said when it was settled, if they said anything. */
  note: string | null;
  /** A decision's options to choose from, NSB's words on the master for the client (design study 22); none for a decision without. */
  options?: TreeOption[];
  /** Whether one of them is chosen, or any number. */
  choose?: DecisionChoose;
  /** Once decided, what was chosen, as the options read when chosen; null while it is not, or for a decision without options. */
  chosen?: TreeOption[] | null;
}

/** The client's Decisions page (Still to decide, A): waiting on them, coming up, and made. */
export interface ClientDecisionsDto {
  /** NSB shows the client the process; off, there is nothing here. */
  shown: boolean;
  waiting: ClientDecisionDto[];
  coming: ClientDecisionDto[];
  made: ClientDecisionDto[];
}

/** A thing waiting on a client that the portal emails them about (spec "Client Emails" §2), and so can remind them of. */
export const REMIND_KINDS = ['review', 'budget', 'step', 'deviation', 'patterns'] as const;
export type RemindKind = (typeof REMIND_KINDS)[number];

/**
 * How one person on the client's side was told of a thing waiting on them (design study 26): emailed (its day), on
 * their Waiting on you since they were invited (`invited`: it was waiting when their welcome went), going out by their
 * email setting (`waiting`), its email tried and not gone (`not_sent`), or no sign-in to send it to.
 */
export type ToldState = 'emailed' | 'invited' | 'waiting' | 'not_sent' | 'no_sign_in';

export interface ToldPersonDto {
  name: string;
  /** Their contact on the project: a reviewer's row is theirs. Null for someone who signs in with no contact row. */
  contactId: string | null;
  told: ToldState;
  /** When: emailed, invited, or tried; null while it waits on their setting, and with no sign-in. */
  at: string | null;
  /** The last time NSB reminded them of it, this time it is waiting. */
  remindedAt: string | null;
  /** Emailed or invited, and not reminded today (Eastern): NSB may remind them now. */
  canRemind: boolean;
}

/** One thing waiting on the client and who of them was told of it, for NSB's own pages (`GET /projects/:id/told`). */
export interface ToldDto {
  kind: RemindKind;
  subjectId: string;
  people: ToldPersonDto[];
}

/** What a reminder did: who it reached, and who it did not, with why. */
export interface RemindResultDto {
  reminded: string[];
  failed: { name: string; error: string }[];
  told: ToldDto[];
}

export interface ClientProcessStepDto {
  id: string;
  key: string | null;
  kind: 'work' | 'milestone' | 'decision' | 'inspection' | 'gate';
  /** The step's one name, for the client and NSB alike (design study 31, slice 3). */
  title: string;
  parentId: string | null;
  phase: PhaseKey;
  state: 'done' | 'now' | 'next' | 'ahead';
  /** What NSB shared of its log, its tasks' with it, newest first (design study 31, slice 3). */
  log: ClientLogEntryDto[];
  /** Where it stands, in a word or two. */
  word: string;
  /** Whose it is, in the client's words; a gate's is who approves it, null for one nobody approves (it happens). */
  owner: 'you' | 'nsb' | 'others' | null;
  why: string | null;
  doneOn: string | null;
  /** About when it finishes, for a step not done. */
  finish: string | null;
  /** On the chain that sets the end date. */
  critical: boolean;
  icon: string | null;
  /** It waits on the client now: theirs, and the work has reached it, as their Waiting on you lists it (core `clientSteps`). */
  yours: boolean;
  /**
   * The work has reached it (core `workReached`): everything it waits on is done and nothing holds it. Not started, it is
   * ready to start (Working on, design study 31).
   */
  reached: boolean;
  /** The day planned for it, where someone planned one: the funnel's date for it (core schedule `plannedOn`). */
  plannedOn: string | null;
  /**
   * A task done in an earlier phase than its step (design study 31, slice 4; core `earlyTasks`): the step at its top it
   * is done for. Drawn in its own phase (`phase`) as a small tile naming that step, and on Working on in its own right.
   */
  early?: { stepId: string } | null;
  /**
   * A gate done: approved, and by whom, or marked done (design study 09, gates A). `by` is a first name, only ever the
   * client's or NSB's, never a sub's or supplier's; `you` when it was the one reading. `recorded`: the client's approval
   * given outside the portal, recorded by NSB (design study 24, L-16 A): approved by the client, by "you" to a client,
   * never NSB's name or its note.
   */
  passed?: { approved: boolean; by: string | null; you: boolean; recorded?: boolean } | null;
}

export interface ClientProcessDto {
  /** NSB shows the client the process; off, there is nothing else here. */
  shown: boolean;
  today: string;
  steps: ClientProcessStepDto[];
  links: ProcessLinkDto[];
  /** The phases as the tree names them, in order (core `phasesOf`). */
  phases?: { key: PhaseKey; name: string; what: string }[];
  /** The steps that close a phase, by key (core `phaseClosers`): each drawn alone at its phase's foot. */
  closers?: string[];
  /** The tree's start (Project setup), by key: drawn above the phases, as the master draws it. */
  start?: string | null;
}

/**
 * A job's process changes held until saved (Still to decide, A): how many, each in its words, and since when. The job
 * is shown with them on it; nothing about it changes until Save the changes.
 */
export interface ProcessDraftDto {
  count: number;
  changes: string[];
  openedAt: string;
  openedBy: string | null;
  /**
   * The change in it that no longer fits the job as it is (something it relies on changed under it), in words, and
   * why: the job is then shown as it is, without the draft, until that change is taken back or the draft discarded.
   * Null while every change fits.
   */
  stale: string | null;
}

/** A project's process as staff see and edit it, with the tree it grows from. */
export interface ProcessDto {
  /** Null until it is configured (setup, step 2). */
  kind: 'client' | 'internal' | null;
  steps: ProcessStepDto[];
  links: ProcessLinkDto[];
  /** The tree's steps left out of this job (design study 07), by key: NSB's alone, passed over, offered again only once put back. */
  out: string[];
  /** YYYY-MM-DD, NSB's time zone: the schedule is planned from here. */
  today: string;
  /** The master process tree's version it reads, as last saved. */
  treeVersion: number;
  /** When that version was saved, ISO; null when it was not saved from the process tree page. */
  treeSavedAt: string | null;
  /** The tree it grows from: the master as last saved, with this project's own settings over it (core `withOverrides`). */
  tree: ProcessTreeDoc;
  /** This project's own settings on the master's steps, by step key (migration 0057): what it does not follow the master on. */
  overrides: NodeOverrides;
  /** Its draft, when its Process page holds changes not saved yet; asked for with `?draft=1`. */
  draft?: ProcessDraftDto | null;
  /**
   * How much of its process is kept as it ended (L-19 A, core `processKept`): `shape` once it is completed (nothing
   * added, filed away, left out or redrawn; the steps it has still go on to done), `whole` once it is archived (nothing
   * on it changes); null while it runs, or cancelled and not yet archived. Its words are core `keptWords`.
   */
  kept: ProcessKept | null;
  /**
   * Closed to its client, cancelled (archived or not) or archived (core `clientClosed`): nothing waits on them or comes
   * up for them, so a step of theirs reads as not done, never as on their Waiting on you, as their home and their
   * Decisions page read it. Absent while the job runs, or once it is completed.
   */
  clientClosed?: boolean;
  /**
   * Who signs in to the job as its client, by name (design study 24, L-20 B): while nobody does, NSB confirms or
   * declines a deviation for the client; once someone does, only they answer. Empty on NSB's own job.
   */
  clientSignIns?: string[];
}

/** Where locking the process tree in stands (API domain/lock-in.ts). */
export interface LockInStateDto {
  /** The master tree version jobs are made from. */
  version: number;
  /** The process tree's draft version it was locked in from; null before the first lock-in. */
  from: number | null;
  /** The draft's version now; null before it is first saved. */
  draft: number | null;
  /** When it was locked in, ISO; null before the first lock-in. */
  at: string | null;
  /** What saving the tree would change for projects, in words (core `treeChanges`); empty when nothing would. */
  changes: string[];
  /** Why the tree cannot be saved for projects as it stands, or null. */
  refused: string | null;
}

export interface LockInResultDto {
  version: number;
  from: number;
  /** What the old tree had that found no place in the new one, in words. */
  left: string[];
  /** How many projects had steps follow it (design study 25): their steps that follow the master took its changes. */
  followed: number;
}

/**
 * A step of the master as its card has it, on a project or in the master (design study 25, q-what B): what a project
 * can change of it, and carry back. Its checklist by its items' words.
 */
export interface StepCardDto {
  title: string;
  /** What it is: NSB's. */
  why: string | null;
  /** What the client reads when they open it; empty or null, its tasks only. */
  clientNote: string | null;
  owner: TaskOwner | null;
  trade: string | null;
  /** The switches the master's card gives its kind, as they stand. */
  switches: Partial<Record<OverrideField, boolean>>;
  list: string[];
}

/**
 * Where a step of the master stands on the other projects that have it, for the warning before the master changes from
 * a project (design study 25, q-warn B): they will follow it; they keep their own (anything on that step changed and
 * saved there); they have it filed away (it follows while filed, and comes back with it); it is started or done there
 * (the new words reach it, work the change adds or takes away does not).
 */
export type CarryGroup = 'follow' | 'keep' | 'filed' | 'started';

export interface StepMasterDto {
  /** This project's step follows the master, or keeps its own since when (ISO). */
  follows: boolean;
  since: string | null;
  /** The master's card for it as it stands; null when the master no longer has it. */
  master: StepCardDto | null;
  /** Every other project that has the step and is not completed or archived, by group; a test project marked. */
  projects: { id: string; name: string; test: boolean; group: CarryGroup; clientReads: boolean }[];
  /**
   * Why the master cannot be updated from here now, in words: its own changes not yet saved for projects (q-pending B),
   * or it does not hold as it stands. Null when it can.
   */
  pending: string | null;
}

/** A step carried back to the master (design study 25): the master's new version, and how many projects followed it. */
export interface CarryBackResultDto {
  version: number;
  followed: number;
}

/**
 * Whose clients carrying a step back would tell (Jason, 2026-10-08, "Let it notify, name it"): each project where a step
 * would newly wait on its client once the master changes (a step that follows it made the client's, the work having
 * reached it there), by the rule their Waiting on you and their emails read, so the warning names them and says they
 * will be told; this project among them, first (`here`), its card saved on its step at once. Read by making the
 * carry-back and rolling it back: nothing of it is kept. Steps by NSB's names.
 */
export interface CarryToldDto {
  told: { id: string; name: string; test: boolean; here: boolean; steps: string[] }[];
}

// --- Trades and performers (tech-tree spec v2 §4): the org's list, edited in Settings ----------

export interface TradeDto {
  id: string;
  key: string;
  name: string;
  /** Its CSI MasterFormat number: a division ("06") or a section in one ("06 13 00"). Shown, never a key. */
  csi: string | null;
  /** NSB's own, not a row of the CSI trades table (whose rows keep the table's number and name). */
  custom: boolean;
  /** On the quick list: a painted picture, and the trades a sub is given when added. */
  keyTrade: boolean;
  /** Its painted picture, when there is one (every trade has one painted: L-15). */
  icon: string | null;
  /** Where the lines of projects stamped from the old template print; never a key. */
  printCode: number;
  printOrder: number;
  archived: boolean;
}

export interface PerformerDto {
  id: string;
  name: string;
  kind: PerformerKind;
  /** Trade keys this performer does. */
  trades: string[];
  email: string | null;
  phone: string | null;
  notes: string | null;
  archived: boolean;
}

export interface TradesDto {
  trades: TradeDto[];
  performers: PerformerDto[];
  /** The trades' pictures: being painted, still missing, or why none can be painted. */
  pictures: { painting: number; missing: number; blocked: string | null };
}
